import React, { useState } from "react";
import CesiumJS from "./CesiumJS";
import styled from "styled-components";
import { GlassmorphismContainer } from "../components/styled/shared/Glassmorphism";
import { Link, useNavigate } from "react-router-dom";
import { postLogin } from "../api/userManagementAPI";
import { useCustomAlertStore, useUserStore } from "../Store/store";

const LoginContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  .cesium-container {
    pointer-events: none;
  }
`;

const LoginHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  pointer-events: none;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginBox = styled(GlassmorphismContainer)`
  width: 400px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  pointer-events: auto;
`;

const Title = styled.h1`
  color: white;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  margin-bottom: 10px;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const InputGroup = styled.div`
  position: relative;
  width: 100%;
`;

const InputLabel = styled.label`
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  display: block;
`;

const InputField = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 40px 12px 0;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.7);
  color: white;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;
  position: relative;
  z-index: 1;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    border-bottom-color: white;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  font-size: 18px;
`;

const OptionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ForgotPassword = styled(Link)`
  color: #4a90e2;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const RegisterLink = styled.div`
  text-align: center;
  color: white;
  font-size: 14px;

  a {
    color: #4a90e2;
    text-decoration: none;
    font-weight: 600;
    margin-left: 5px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "admin@datamatica.kr",
    password: "testpass123",
  });
  const { showErrorAlert, showSuccessAlert } = useCustomAlertStore();
  const { login } = useUserStore();

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await postLogin(formData.email, formData.password);

      const userInfo = {
        company: response.company,
        platformId: response.platformId,
        expirationMinutes: response.expirationMinutes,
      };

      login(response.token, userInfo);

      // useUserStore의 login 함수를 사용하여 상태 업데이트
      // login(response.token, response.roles || { email: formData.email });

      // 바로 홈으로 이동 (모달 없이)
      navigate("/");
    } catch (error) {
      console.error("Login failed:", error);
      showErrorAlert(
        "로그인 실패",
        "이메일 또는 비밀번호가 일치하지 않습니다.",
      );
    }
  };

  return (
    <LoginContainer>
      <LoginHeader>
        <LoginBox>
          <Title>login</Title>

          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <InputLabel>이메일 주소</InputLabel>
              <InputField>
                <Input
                  type="email"
                  name="email"
                  placeholder="이메일 주소를 입력해주세요"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <InputIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    fill="currentColor"
                    className="bi bi-envelope"
                    viewBox="0 0 16 16"
                  >
                    <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1zm13 2.383-4.708 2.825L15 11.105zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741M1 11.105l4.708-2.897L1 5.383z" />
                  </svg>
                </InputIcon>
              </InputField>
            </InputGroup>

            <InputGroup>
              <InputLabel>비밀번호</InputLabel>
              <InputField>
                <Input
                  type="password"
                  name="password"
                  placeholder="비밀번호를 입력해주세요"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                <InputIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    fill="currentColor"
                    className="bi bi-key"
                    viewBox="0 0 16 16"
                  >
                    <path d="M0 8a4 4 0 0 1 7.465-2H14a.5.5 0 0 1 .354.146l1.5 1.5a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0L13 9.207l-.646.647a.5.5 0 0 1-.708 0L11 9.207l-.646.647a.5.5 0 0 1-.708 0L9 9.207l-.646.647A.5.5 0 0 1 8 10h-.535A4 4 0 0 1 0 8m4-3a3 3 0 1 0 2.712 4.285A.5.5 0 0 1 7.163 9h.63l.853-.854a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.793-.793-1-1h-6.63a.5.5 0 0 1-.451-.285A3 3 0 0 0 4 5" />
                    <path d="M4 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                  </svg>
                </InputIcon>
              </InputField>
            </InputGroup>

            <OptionsRow>
              <ForgotPassword to="/find-password">비밀번호 찾기</ForgotPassword>
            </OptionsRow>

            <LoginButton type="submit">로그인</LoginButton>
          </Form>

          <RegisterLink>
            아직 회원이 아니신가요?<Link to="/register">회원가입</Link>
          </RegisterLink>
        </LoginBox>
      </LoginHeader>
      <div className="cesium-container">
        <CesiumJS />
      </div>
    </LoginContainer>
  );
}
